import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import MailListRow from './MailListRow';
import { formatMailDate } from '../utils/mailViewHelpers';
import './Drafts.css';
import './Inbox.css';
import './FolderView.css';

/**
 * Drafts Component
 * Displays all saved drafts for the logged-in user
 * Features:
 * - List all drafts sorted by most recently updated
 * - Click to continue editing a draft
 * - Delete draft functionality
 */
const Drafts = ({ currentUser, onLoadDraft }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [selectedDraftIds, setSelectedDraftIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Selection handlers
  const handleSelectDraft = (draftId) => {
    setSelectedDraftIds(prev => 
      prev.includes(draftId) 
        ? prev.filter(id => id !== draftId)
        : [...prev, draftId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDraftIds.length === drafts.length) {
      setSelectedDraftIds([]);
    } else {
      setSelectedDraftIds(drafts.map(d => d._id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedDraftIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedDraftIds.length === 0) {
      setAlertMessage('Please select at least one draft to delete');
      setShowAlert(true);
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedDraftIds.length === 0) return;
    
    setShowBulkDeleteConfirm(false); // Close modal immediately

    try {
      const deletePromises = selectedDraftIds.map(id => 
        axiosInstance.delete(`/api/mail/draft/${id}`)
      );
      await Promise.all(deletePromises);
      
      setDrafts(drafts.filter(d => !selectedDraftIds.includes(d._id)));
      setSelectedDraftIds([]);
      fetchDrafts();
    } catch (error) {
      console.error('Error deleting drafts:', error);
      setAlertMessage('Error deleting drafts. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkDownload = () => {
    if (selectedDraftIds.length === 0) return;
    const selectedDraftsList = drafts.filter(d => selectedDraftIds.includes(d._id));
    selectedDraftsList.forEach(draft => {
      const draftContent = `To: ${draft.to || ''}\nSubject: ${draft.subject || ''}\nDate: ${formatDate(draft.updatedAt)}\n\n${draft.body || ''}`;
      const blob = new Blob([draftContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${draft.subject || 'draft'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  /**
   * Fetch drafts when component mounts
   */
  useEffect(() => {
    fetchDrafts();
    
    // Auto-refresh drafts every 30 seconds (without showing loading spinner)
    const interval = setInterval(() => {
      fetchDrafts(false);
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  /**
   * Fetch drafts from backend
   */
  const fetchDrafts = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');
    try {
      const response = await axiosInstance.get('/api/mail/drafts');
      if (response.data.success) {
        setDrafts(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch drafts');
      }
    } catch (error) {
      console.error('Error fetching drafts:', error);
      setError(error.response?.data?.message || 'Error fetching drafts. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  /**
   * Handle draft click - load draft for editing
   */
  const handleDraftClick = (draft) => {
    if (onLoadDraft) {
      onLoadDraft(draft);
    }
    navigate('/compose');
  };

  /**
   * Handle delete draft
   */
  const handleDeleteDraft = async (e, draftId) => {
    e.stopPropagation(); // Prevent triggering draft click
    setDraftToDelete(draftId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;

    const deletingId = draftToDelete;
    setShowDeleteConfirm(false);

    try {
      const response = await axiosInstance.delete(`/api/mail/draft/${deletingId}`);
      if (response.data.success) {
        // Remove draft from list
        setDrafts(drafts.filter(d => d._id !== deletingId));
      } else {
        setAlertMessage(response.data.message || 'Failed to delete draft');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      setAlertMessage(error.response?.data?.message || 'Error deleting draft. Please try again.');
      setShowAlert(true);
    } finally {
      setDraftToDelete(null);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading drafts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchDrafts} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="inbox-container premium-mail-dashboard">
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>Drafts</h2>
        </div>
        <div className="inbox-header-actions">
          <p className="mail-count">
            {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}
          </p>
        </div>
      </div>

      <div className="inbox-content reading-pane-no-split gmail-split-layout">
        <div className="mail-list-container full-width">
      {drafts.length > 0 && (
        <div className="mail-toolbar">
          <div className="toolbar-left">
            <button 
              className="toolbar-btn checkbox-btn"
              onClick={selectedDraftIds.length > 0 ? handleDeselectAll : handleSelectAll}
              title={selectedDraftIds.length > 0 ? "Deselect all" : "Select all"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                {selectedDraftIds.length > 0 && (
                  <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round"/>
                )}
              </svg>
              {selectedDraftIds.length > 0 && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              )}
            </button>
            
            <button 
              className="toolbar-btn"
              onClick={fetchDrafts}
              title="Refresh"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6"/>
                <path d="M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            
            {selectedDraftIds.length > 0 && (
              <>
                <button 
                  className="toolbar-btn"
                  onClick={handleBulkDownload}
                  title="Download"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
                
                <button 
                  className="toolbar-btn"
                  onClick={handleBulkDelete}
                  title="Delete"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="mail-list">
        {drafts.length === 0 ? (
          <div className="empty-inbox">
            <p>You have no saved drafts</p>
            <p className="empty-hint">Start composing a mail to create a draft</p>
          </div>
        ) : (
          drafts.map((draft) => (
            <MailListRow
              key={draft._id}
              mail={draft}
              mailKey={draft._id}
              avatarSeed={draft.to || 'draft'}
              senderText={draft.to ? `To: ${draft.to}` : 'No recipient'}
              dateText={formatMailDate(draft.updatedAt)}
              isChecked={selectedDraftIds.includes(draft._id)}
              isUnread={false}
              onOpen={handleDraftClick}
              onToggleCheck={handleSelectDraft}
              actions={[
                {
                  key: 'delete',
                  title: 'Delete draft',
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  ),
                  onClick: (e, d) => handleDeleteDraft(e, d._id),
                },
              ]}
            />
          ))
        )}
      </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDraftToDelete(null);
        }}
        onConfirm={confirmDeleteDraft}
        message="Are you sure you want to delete this draft?"
        confirmText="OK"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to delete ${selectedDraftIds.length} draft(s)?`}
        confirmText="OK"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  );
};


export default Drafts;

